import type { Family, FamilyChild, FamilyGroup } from "@teamna/shared";
import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { Avatar, Button, Select, showToast } from "../../components/index.ts";
import { GroupDot } from "./bits.tsx";
import { useHoldDrag } from "./hold-drag.ts";
import { ageRangesText, capital, columnOf, firstName, groupLine, placeByAge, type BoardColumn, type Words } from "./model.ts";
import type { FamilyActions } from "./types.ts";

/**
 * The groups board: one column per group, plus "No group yet" and "Not sure yet". Only the group
 * matters here: a member sees the sessions of their child's group and books the ones they want.
 *
 * Three ways to move a card: the "Move to" menu on the card, choose several and press "Move here"
 * on a column, or hold a card and drag it (`useHoldDrag`: touch and mouse, the whole box is the drop
 * area; #ai-feedback 2026-10-26). The card slides to its new column (decided 2026-10-03: the owner
 * must see where it went), unless the person asked for less motion. Every move has Undo.
 */

type Move = { participantId: string; name: string; from: BoardColumn; to: BoardColumn };

function targetOf(column: BoardColumn): string | "unsure" | null {
  return column === "none" ? null : column;
}

function reducedMotion(): boolean {
  try {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return false;
  }
}

export function GroupsBoard({
  families,
  groups,
  words,
  actions,
  onAddList,
  onError,
}: {
  families: Family[];
  groups: FamilyGroup[];
  words: Words;
  actions: FamilyActions;
  /** "Add this group's list": opens Add families with the group chosen. */
  onAddList: (groupId: string) => void;
  onError: (error: unknown) => void;
}) {
  // A move shows at once. Each entry remembers where the server had the card, so the card follows
  // the server again as soon as it reports the move (or anything else).
  const [pending, setPending] = useState<Record<string, { from: BoardColumn; to: BoardColumn }>>({});
  const [flipTick, setFlipTick] = useState(0);
  const [selected, setSelected] = useState<string[]>([]);
  const [arrived, setArrived] = useState<string[]>([]);
  const boardRef = useRef<HTMLDivElement>(null);
  const flipFrom = useRef<Map<string, DOMRect>>(new Map());
  const cardRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const arriveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const kids = useMemo(
    () => families.flatMap((family) => family.children.map((child) => ({ child, family }))),
    [families],
  );
  const columnFor = (child: FamilyChild): BoardColumn => {
    const server = columnOf(child, groups);
    const waiting = pending[child.participantId];
    return waiting && server === waiting.from ? waiting.to : server;
  };

  // FLIP: each moved card starts where it was and slides to where it is now.
  useLayoutEffect(() => {
    if (flipTick === 0 || flipFrom.current.size === 0) return;
    const from = flipFrom.current;
    flipFrom.current = new Map();
    if (reducedMotion()) return;
    for (const [id, rect] of from) {
      const element = cardRefs.current.get(id);
      if (!element || typeof element.animate !== "function") continue;
      const now = element.getBoundingClientRect();
      const dx = rect.left - now.left;
      const dy = rect.top - now.top;
      if (!dx && !dy) continue;
      element.animate(
        [
          { transform: `translate(${dx}px, ${dy}px) scale(1.03)`, boxShadow: "0 12px 28px rgba(17,26,43,.22)", zIndex: 5 },
          { transform: "translate(0, 0) scale(1)", boxShadow: "0 1px 2px rgba(17,26,43,.06)", zIndex: 5 },
        ],
        { duration: 560, easing: "cubic-bezier(.2,.8,.2,1)" },
      );
    }
  }, [flipTick]);

  /** `startRects`: where a card starts its slide, when not its place on the board (a dragged card). */
  function move(ids: string[], to: BoardColumn, startRects?: Map<string, DOMRect>) {
    const moves: Move[] = [];
    for (const id of ids) {
      const row = kids.find((kid) => kid.child.participantId === id);
      if (!row) continue;
      const from = columnFor(row.child);
      if (from === to) continue;
      moves.push({ participantId: id, name: row.child.name, from, to });
    }
    if (moves.length === 0) return;
    apply(moves, true, startRects);
  }

  function apply(moves: Move[], offerUndo: boolean, startRects?: Map<string, DOMRect>) {
    const rects = new Map<string, DOMRect>();
    for (const item of moves) {
      const element = cardRefs.current.get(item.participantId);
      const start = startRects?.get(item.participantId) ?? element?.getBoundingClientRect();
      if (start) rects.set(item.participantId, start);
    }
    flipFrom.current = rects;
    setPending((current) => {
      const next = { ...current };
      for (const item of moves) {
        const row = kids.find((kid) => kid.child.participantId === item.participantId);
        next[item.participantId] = { from: row ? columnOf(row.child, groups) : item.from, to: item.to };
      }
      return next;
    });
    setFlipTick((tick) => tick + 1);
    setSelected([]);
    setArrived(moves.map((item) => item.participantId));
    if (arriveTimer.current) clearTimeout(arriveTimer.current);
    arriveTimer.current = setTimeout(() => setArrived([]), 1700);

    void Promise.all(moves.map((item) => actions.place(item.participantId, targetOf(item.to)))).catch((error: unknown) => {
      // The failed ones go back where the server has them.
      setPending((current) => {
        const next = { ...current };
        for (const item of moves) delete next[item.participantId];
        return next;
      });
      onError(error);
    });

    if (offerUndo) {
      const where = columnTitle(moves[0]!.to);
      const who = moves.length === 1 ? moves[0]!.name : `${moves.length} ${words.participants}`;
      showToast(`${who} moved to ${where}.`, {
        action: {
          label: "Undo",
          onClick: () => apply(moves.map((item) => ({ participantId: item.participantId, name: item.name, from: item.to, to: item.from })), false),
        },
      });
    }
  }

  function columnTitle(column: BoardColumn): string {
    if (column === "none") return `No ${words.group} yet`;
    if (column === "unsure") return "Not sure yet";
    return groups.find((group) => group.id === column)?.name ?? capital(words.group);
  }

  function byAge() {
    const moves = placeByAge(families, groups).map((item) => ({
      participantId: item.participantId,
      name: item.name,
      from: "none" as BoardColumn,
      to: item.groupId,
    }));
    if (moves.length === 0) {
      showToast(`No ${words.participant} with an age is waiting, or no ${words.group} has ${words.participants} of that age yet.`, { tone: "info" });
      return;
    }
    apply(moves, false);
    showToast(`${moves.length} ${moves.length === 1 ? words.participant : words.participants} placed by age. Check them.`, {
      action: {
        label: "Undo",
        onClick: () => apply(moves.map((item) => ({ participantId: item.participantId, name: item.name, from: item.to, to: "none" })), false),
      },
    });
  }

  const columns: Array<{ key: BoardColumn; title: string; sub: string; group: FamilyGroup | null }> = [
    { key: "none", title: `No ${words.group} yet`, sub: `Their ${words.members} see no sessions for them yet.`, group: null },
    ...groups.map((group) => ({ key: group.id, title: group.name, sub: groupLine(group, words), group })),
    { key: "unsure", title: "Not sure yet", sub: `Ask the ${words.staff} after a trial. The ${words.member} reads "${words.group} soon".`, group: null },
  ];
  const ranges = ageRangesText(families, groups);

  const drag = useHoldDrag({ board: boardRef, onDrop: (id, column, ghostRect) => move([id], column, new Map([[id, ghostRect]])) });

  return (
    <section aria-label={capital(words.groups)} className="flex flex-col gap-4">
      <div className="flex flex-col gap-3 rounded-card bg-accent-surface px-4 py-3 text-accent-strong sm:flex-row sm:items-center">
        <p className="min-w-0 flex-1 text-[14px] leading-relaxed">
          Only the {words.group} matters. {capital(words.members)} see the sessions of their child's {words.group} and book the ones
          they want. Use the "Move to" menu on a card, choose several cards and press "Move here", or hold a card and drag it.
        </p>
        <Button variant="secondary" onClick={byAge} title={ranges ? `By the ages already placed: ${ranges}` : undefined}>
          Place by age
        </Button>
      </div>

      <div ref={boardRef} className="grid gap-3 sm:grid-cols-2 xl:grid-cols-[repeat(auto-fit,minmax(220px,1fr))]">
        {columns.map((column) => {
          const inColumn = kids.filter((kid) => columnFor(kid.child) === column.key);
          // Null is no limit: no "of N", no bar, and never over.
          const capacity = column.group?.capacity ?? null;
          const over = column.group && capacity !== null ? inColumn.length - capacity : 0;
          const showMove = selected.length > 0 && selected.some((id) => !inColumn.some((kid) => kid.child.participantId === id));
          const isOver = drag.over === column.key;
          return (
            <div
              key={column.key}
              data-board-column={column.key}
              className={[
                "flex min-h-[180px] flex-col gap-2.5 rounded-card p-3 transition-shadow duration-150",
                isOver
                  ? "bg-accent-surface ring-[3px] ring-accent"
                  : column.key === "none"
                    ? "bg-sunken ring-1 ring-line"
                    : column.key === "unsure"
                      ? "bg-late-surface ring-1 ring-late-line"
                      : "bg-paper ring-1 ring-line shadow-card",
              ].join(" ")}
            >
              <div className="flex flex-col gap-1">
                <div className="flex items-center gap-2">
                  {column.group ? <GroupDot color={column.group.color} /> : null}
                  <h3 className="min-w-0 flex-1 text-[15px] font-bold text-ink">{column.title}</h3>
                  {isOver ? <span className="text-[12.5px] font-bold text-accent-strong">Drop here</span> : null}
                  <span className={`text-[13px] font-semibold ${over > 0 ? "text-cancelled-ink" : "text-ink-2"}`}>
                    {column.group && capacity !== null ? `${inColumn.length} of ${capacity}${over > 0 ? `, ${over} over` : ""}` : inColumn.length}
                  </span>
                </div>
                <p className="text-[12.5px] leading-snug text-ink-2">{column.sub}</p>
                {column.group ? (
                  <>
                    {capacity !== null ? (
                      <div className="mt-1 h-1.5 overflow-hidden rounded-pill bg-sunken" aria-hidden="true">
                        <div
                          className="h-full rounded-pill transition-[width] duration-500"
                          style={{
                            width: `${Math.min(100, Math.round((inColumn.length / Math.max(1, capacity)) * 100))}%`,
                            background: over > 0 ? "var(--color-cancelled)" : (column.group.color ?? "var(--color-accent)"),
                          }}
                        />
                      </div>
                    ) : null}
                    <div>
                      <Button variant="ghost" onClick={() => onAddList(column.group!.id)}>
                        Add this {words.group}'s list
                      </Button>
                    </div>
                  </>
                ) : null}
              </div>
              {showMove ? (
                <Button onClick={() => move(selected, column.key)} fullWidth>
                  Move here
                </Button>
              ) : null}
              <div className="flex flex-col gap-2">
                {inColumn.map(({ child, family }) => {
                  const isSelected = selected.includes(child.participantId);
                  return (
                    <div
                      key={child.participantId}
                      ref={(element) => {
                        if (element) cardRefs.current.set(child.participantId, element);
                        else cardRefs.current.delete(child.participantId);
                      }}
                      data-participant={child.participantId}
                      {...drag.cardHandlers(child.participantId)}
                      className={[
                        // A hold lifts the card: no text selection and no phone menu on the card; a
                        // vertical swipe still scrolls the page.
                        "relative flex cursor-grab touch-pan-y select-none flex-col gap-2 rounded-xl p-2.5 [-webkit-touch-callout:none]",
                        drag.pressing === child.participantId ? "scale-[.98] transition-transform duration-200" : "",
                        drag.lifted === child.participantId ? "opacity-35" : "",
                        isSelected
                          ? "bg-accent-surface ring-2 ring-accent"
                          : child.nameMissing
                            ? "border border-dashed border-late-line bg-paper"
                            : "bg-paper shadow-[0_1px_2px_rgba(17,26,43,.06)] ring-1 ring-line",
                        arrived.includes(child.participantId) ? "motion-safe:animate-[card-arrive_1.6s_ease-out]" : "",
                      ].join(" ")}
                    >
                      <button
                        type="button"
                        onClick={() =>
                          setSelected((current) =>
                            current.includes(child.participantId) ? current.filter((id) => id !== child.participantId) : [...current, child.participantId],
                          )
                        }
                        aria-pressed={isSelected}
                        title="Click to choose. Hold and drag to move."
                        className="flex flex-col items-start text-left"
                      >
                        <span className="flex w-full items-center justify-between gap-2">
                          <span className="flex min-w-0 items-center gap-2">
                            <Avatar name={child.name} photoUrl={child.photoUrl ?? null} size={28} />
                            <span className="font-semibold text-ink" dir="auto">
                              {child.name}
                            </span>
                          </span>
                          <span className="text-[12.5px] text-ink-2">
                            {child.nameMissing ? "name not set" : child.age !== null ? `age ${child.age}` : ""}
                          </span>
                        </span>
                        <span className="text-[12.5px] text-ink-2">
                          {capital(words.member)}: {family.adults.map((adult) => firstName(adult.name)).join(" and ")}
                        </span>
                      </button>
                      <Select
                        label={`Move ${child.name} to`}
                        value={columnFor(child)}
                        onChange={(event) => move([child.participantId], event.target.value)}
                      >
                        <option value="none">Move to: No {words.group} yet</option>
                        {groups.map((group) => (
                          <option key={group.id} value={group.id}>
                            Move to: {group.name}
                          </option>
                        ))}
                        <option value="unsure">Move to: Not sure yet</option>
                      </Select>
                    </div>
                  );
                })}
                {inColumn.length === 0 ? (
                  <p className="rounded-xl border border-dashed border-line-strong px-3 py-4 text-center text-[13px] text-ink-2">
                    {column.key === "none" ? `Everyone has a ${words.group}.` : `Drop ${words.participants} here.`}
                  </p>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>

      {selected.length > 0 ? (
        <div className="sticky bottom-4 z-10 flex flex-wrap items-center gap-3 self-center rounded-card bg-ink px-4 py-2.5 text-inverse shadow-bar">
          <span className="font-semibold">
            {selected.length === 1 ? `1 ${words.participant} chosen` : `${selected.length} ${words.participants} chosen`}
          </span>
          <span className="text-[13px] opacity-80">Press "Move here" on a {words.group}.</span>
          <button type="button" onClick={() => setSelected([])} className="min-h-10 rounded-lg px-2 font-semibold underline">
            Clear
          </button>
        </div>
      ) : null}
    </section>
  );
}
